import { useEffect, useRef } from 'react';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { useSetAtom } from 'jotai';
import { api } from 'lib/request/api';
import { selectedDcaScenarioAtom } from 'components/well-page-content';
import type { DcaScenariosResponse } from 'types/api-responses';

const DCA_SCENARIO_QUERY_KEY = 'dca-scenario' as const;

/**
 * The read of one DCA scenario by id.
 *
 * @param {number | null | undefined} id - The scenario to read; nothing is read when empty.
 * @return {object} The query options, keyed by id.
 */
const dcaScenarioQueryOptions = (id: number | null | undefined) =>
  queryOptions({
    queryKey: [DCA_SCENARIO_QUERY_KEY, id] as const,
    queryFn: ({ signal }) =>
      api.get(`dca_scenarios/${id}`, { signal }).json<DcaScenariosResponse>(),
    enabled: id !== null && id !== undefined,
  });

/**
 * Select a page's own DCA scenario in the page-wide `selectedDcaScenarioAtom`,
 * such as a Type Curve Scenario's Well Control DCA Scenario or a DCA
 * workspace's saved scenario. The scenario is read by id, because the DCA
 * scenario selector lists only the 50 newest, and because this works whether
 * or not the selector is on the page.
 *
 * It replaces whatever is selected, such as a scenario carried over from
 * another page or the selector's "pdp default", but only once per `pageKey`,
 * so a scenario the user picks afterwards stays selected until the page moves
 * to another record. Does nothing while `id` is empty.
 *
 * @param {number | null | undefined} id - The scenario to select.
 * @param {string | number | undefined} pageKey - Identifies the record the page shows, e.g. the route id.
 * @return {{ isError: boolean }} Whether the scenario could not be read, so the page can fall back to "pdp default".
 */
export function useSeedDcaScenario(
  id: number | null | undefined,
  pageKey: string | number | undefined
): { isError: boolean } {
  const { data: scenario, isError } = useQuery(dcaScenarioQueryOptions(id));
  const setSelectedDcaScenario = useSetAtom(selectedDcaScenarioAtom);
  const seededKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!scenario || pageKey === undefined) return;

    const key = `${pageKey}:${scenario.id}`;
    if (seededKeyRef.current === key) return;

    seededKeyRef.current = key;
    setSelectedDcaScenario(scenario);
  }, [scenario, pageKey, setSelectedDcaScenario]);

  return { isError };
}
